import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  AtendimentoDetalhe,
  AtributoDetalhe,
  ClinusAtendimento,
  ExameImagem,
} from '@/types/api';

/**
 * Lista atendimentos (consultas) de um paciente.
 * Auto-refetch a cada 60s (refetchInterval).
 *
 * PROMPT 06: auth via cookie httpOnly — sem header Authorization.
 */
export function useAtendimentos(
  cdPaciente: number | null,
  options: { refetchInterval?: number | false } = {},
): UseQueryResult<ClinusAtendimento[], Error> {
  return useQuery({
    queryKey: ['atendimentos', cdPaciente],
    queryFn: ({ signal }) =>
      api.get<ClinusAtendimento[]>(
        `/api/atendimentos?cd_paciente=${cdPaciente}`,
        { signal },
      ),
    enabled: !!cdPaciente,
    refetchInterval: options.refetchInterval ?? 60_000,
    staleTime: 30_000,
  });
}

/**
 * Detalhe de um atendimento (inclui lista de atributos).
 */
export function useAtendimento(
  cdAtendimento: number | null,
): UseQueryResult<AtendimentoDetalhe, Error> {
  return useQuery({
    queryKey: ['atendimento', cdAtendimento],
    queryFn: ({ signal }) =>
      api.get<AtendimentoDetalhe>(`/api/atendimentos/${cdAtendimento}`, {
        signal,
      }),
    enabled: !!cdAtendimento,
  });
}

/**
 * Conteúdo de um atributo (bb_valor base64).
 * Só carrega sob demanda (enabled flag).
 */
export function useAtributo(
  cdAtendimento: number | null,
  cdValor: number | null,
): UseQueryResult<AtributoDetalhe, Error> {
  return useQuery({
    queryKey: ['atributo', cdAtendimento, cdValor],
    queryFn: ({ signal }) =>
      api.get<AtributoDetalhe>(
        `/api/atendimentos/${cdAtendimento}/atributos/${cdValor}`,
        { signal },
      ),
    enabled: !!cdAtendimento && !!cdValor,
    staleTime: 24 * 60 * 60 * 1000, // 24h (RTF é estável)
  });
}


/**
 * Lista exames de imagem e laudos do paciente vindos diretamente do Clinux.
 */
export function useExamesImagem(
  cdPaciente: number | null,
): UseQueryResult<ExameImagem[], Error> {
  return useQuery({
    queryKey: ['exames-imagem', cdPaciente],
    queryFn: ({ signal }) =>
      api.get<ExameImagem[]>(`/api/exames-imagem?cd_paciente=${cdPaciente}`, {
        signal,
      }),
    enabled: !!cdPaciente,
    staleTime: 5 * 60 * 1000,
  });
}
